import { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../hooks/useAuth';
import { ForgotPasswordData } from '../types';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<ForgotPasswordData>({
    email: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      const success = await forgotPassword(formData);
      if (success) {
        setIsSuccess(true);
        toast.success('Password reset email sent successfully!');
      } else {
        toast.error('Email address not found. Please check and try again.');
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setFormData({ email: value });
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl mb-2">Check Your Email</h1>
            <p className="text-muted-foreground">We've sent password reset instructions to your email</p>
          </div>

          {/* Success Message */}
          <div className="bg-card border rounded-lg p-6 shadow-lg">
            <div className="mb-6 p-4 border-green-200 bg-green-50 text-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">
                  If an account with email <strong>{formData.email}</strong> exists, you will receive password reset instructions shortly.
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-muted-foreground space-y-2">
                <p>What to do next:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Check your email inbox</li>
                  <li>Look for an email from PingOwner Admin Panel</li>
                  <li>Click the reset link in the email</li>
                  <li>Follow the instructions to reset your password</li>
                </ul>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>Didn't receive the email? Check your spam folder or try again in a few minutes.</p>
              </div>
            </div>

            {/* Back to Login */}
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full mt-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
            <div className="text-primary-foreground">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="4" width="4" height="4" fill="currentColor"/>
                <rect x="4" y="12" width="4" height="4" fill="currentColor"/>
                <rect x="4" y="20" width="4" height="4" fill="currentColor"/>
                <rect x="12" y="4" width="4" height="4" fill="currentColor"/>
                <rect x="20" y="4" width="4" height="4" fill="currentColor"/>
                <rect x="20" y="12" width="4" height="4" fill="currentColor"/>
                <rect x="28" y="4" width="4" height="4" fill="currentColor"/>
                <rect x="28" y="20" width="4" height="4" fill="currentColor"/>
              </svg>
            </div>
          </div>
          <h1 className="text-2xl mb-2">Forgot Password</h1>
          <p className="text-muted-foreground">Enter your email to receive password reset instructions</p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-card border rounded-lg p-6 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Instructions'}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary hover:underline flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}